import { keyframes } from '@emotion/react';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';
import React from 'react';
import { ConversationExercise, ConversationLine, ConversationMode } from '../../../student/mockLessonContent';
import { BLUE, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SURFACE_SOFT } from '../../../student/studentStyles';
import ExerciseLabel from '../../../student/components/exercises/ExerciseLabel';
import { useExerciseLayout } from './exerciseLayout';
import RecordAnswerPanel from '../../../student/components/exercises/RecordAnswerPanel';

const speakerPulse = keyframes`
    0% { opacity: 1; }
    50% { opacity: 0.45; }
    100% { opacity: 1; }
`;

// How long each line stays highlighted during fake playback.
const LINE_MS = 2200;

const labelByMode: Record<ConversationMode, string> = {
    listen: 'LISTEN',
    listenNoCharacters: 'LISTEN ONLY · NO TEXT',
    listenSpeak: 'LISTEN, THEN SPEAK',
    readSpeak: 'READ & SPEAK',
    readBlankSpeak: 'FILL THE GAPS OUT LOUD',
    speakOnly: 'FROM MEMORY',
};

const speakingModes: ConversationMode[] = ['listenSpeak', 'readSpeak', 'readBlankSpeak', 'speakOnly'];

// Every character replaced, the way the runtime blanks a SpeakOnly line.
function blankOut(text: string): string {
    return text.replace(/./g, '_');
}

function displayHanzi(line: ConversationLine, mode: ConversationMode): string {
    if (!line.isStudent) {
        return line.hanzi;
    }
    if (mode === 'readBlankSpeak') {
        return line.blankHanzi ?? line.hanzi;
    }
    if (mode === 'speakOnly') {
        return blankOut(line.hanzi);
    }
    return line.hanzi;
}

interface ConversationExerciseCardProps {
    exercise: ConversationExercise;
    onReady: () => void;
}

// One card for all six conversation prompt classes — see ConversationMode in
// mockLessonContent. Listening modes need no input; the speaking modes hand the
// student's own lines back to them, blanked out to the degree the mode calls for.
export default function ConversationExerciseCard({ exercise, onReady }: ConversationExerciseCardProps) {
    const desktop = useExerciseLayout() === 'desktop';
    const [playingLine, setPlayingLine] = React.useState<number | null>(null);
    const [playingAll, setPlayingAll] = React.useState(false);
    const [recordedLines, setRecordedLines] = React.useState<number[]>([]);
    const timeoutRef = React.useRef<number>();

    React.useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

    const mode = exercise.mode;
    const isSpeakingMode = speakingModes.includes(mode);
    const hideText = mode === 'listenNoCharacters';
    // Reading and memory modes give no model audio — that would hand over the answer.
    const canPlayAudio = mode === 'listen' || mode === 'listenNoCharacters' || mode === 'listenSpeak';
    const studentLineCount = exercise.lines.filter(line => line.isStudent).length;

    const stopPlayback = () => {
        window.clearTimeout(timeoutRef.current);
        setPlayingAll(false);
        setPlayingLine(null);
    };

    const playFrom = (index: number, all: boolean) => {
        window.clearTimeout(timeoutRef.current);
        if (index >= exercise.lines.length) {
            stopPlayback();
            return;
        }
        setPlayingLine(index);
        timeoutRef.current = window.setTimeout(() => {
            if (all) {
                playFrom(index + 1, true);
            } else {
                setPlayingLine(null);
            }
        }, LINE_MS);
    };

    const handlePlayAll = () => {
        if (playingAll) {
            stopPlayback();
        } else {
            setPlayingAll(true);
            playFrom(0, true);
        }
    };

    const handlePlayLine = (index: number) => {
        setPlayingAll(false);
        playFrom(index, false);
    };

    const handleLineRecorded = (index: number) => {
        if (recordedLines.includes(index)) {
            return;
        }
        const next = [...recordedLines, index];
        setRecordedLines(next);
        if (next.length === studentLineCount) {
            onReady();
        }
    };

    return (
        <Box>
            <ExerciseLabel>{labelByMode[mode]}</ExerciseLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY, flex: 1, lineHeight: 1.3 }}>
                    {exercise.title}
                </Typography>
                {canPlayAudio && (
                    <Button
                        onClick={handlePlayAll}
                        startIcon={playingAll ? <StopRoundedIcon /> : <PlayArrowRoundedIcon />}
                        sx={{
                            fontFamily: INTER, fontWeight: 700, fontSize: 13, color: BLUE,
                            border: `1px solid ${RING_TRACK}`, borderRadius: '999px', px: 1.75, py: 0.5,
                            whiteSpace: 'nowrap', flexShrink: 0,
                        }}
                    >
                        {playingAll ? 'Stop' : 'Play all'}
                    </Button>
                )}
            </Box>
            {isSpeakingMode && (
                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mb: 2 }}>
                    You are {exercise.lines.find(line => line.isStudent)?.speakerEnglish}. Record each of your lines to
                    continue ({recordedLines.length} of {studentLineCount} done).
                </Typography>
            )}
            <Stack spacing={1.5}>
                {exercise.lines.map((line, index) => {
                    const isActive = playingLine === index;
                    const isRight = line.side === 'right';
                    const needsRecording = isSpeakingMode && line.isStudent === true;
                    // Blanked lines drop their pinyin too; a memory line drops the English cue as well.
                    const hidePinyin = line.isStudent === true && (mode === 'readBlankSpeak' || mode === 'speakOnly');
                    const hideEnglish = line.isStudent === true && mode === 'speakOnly';
                    return (
                        <Box key={`${line.hanzi}-${index}`} sx={{ display: 'flex', justifyContent: isRight ? 'flex-end' : 'flex-start' }}>
                            <Box sx={{
                                maxWidth: desktop ? (needsRecording ? '82%' : '72%') : (needsRecording ? '95%' : '85%'),
                                p: desktop ? 2 : 1.75,
                                borderRadius: isRight ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                backgroundColor: isRight ? `${BLUE}14` : SURFACE_SOFT,
                                border: `2px solid ${isActive ? BLUE : 'transparent'}`,
                                transition: 'border-color 150ms ease',
                                display: 'flex', alignItems: 'flex-start', gap: 1,
                            }}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.75}>
                                        <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 12, color: SLATE }}>
                                            {line.speakerHanzi} · {line.speakerEnglish}
                                        </Typography>
                                        {needsRecording && (
                                            <Chip
                                                label="YOUR LINE"
                                                size="small"
                                                sx={{ height: 24, fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '0.06em', color: BLUE, backgroundColor: `${BLUE}1F` }}
                                            />
                                        )}
                                    </Stack>
                                    {hideText ? (
                                        <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.5, fontStyle: 'italic' }}>
                                            Audio only — listen for the meaning
                                        </Typography>
                                    ) : (
                                        <>
                                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 500, fontSize: 16, color: NAVY, mt: 0.25, lineHeight: 1.4, letterSpacing: hidePinyin ? '0.08em' : 'normal' }}>
                                                {displayHanzi(line, mode)}
                                            </Typography>
                                            {!hidePinyin && (
                                                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>
                                                    {line.pinyin}
                                                </Typography>
                                            )}
                                            {!hideEnglish && (
                                                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>
                                                    {line.english}
                                                </Typography>
                                            )}
                                        </>
                                    )}
                                    {needsRecording && (
                                        <RecordAnswerPanel
                                            grades={line.grades ?? []}
                                            idleLabel="Hold to say your line"
                                            compact
                                            onScored={() => handleLineRecorded(index)}
                                        />
                                    )}
                                </Box>
                                {canPlayAudio && (
                                    <IconButton
                                        aria-label={`Play line ${index + 1}`}
                                        onClick={() => handlePlayLine(index)}
                                        sx={{ width: 44, height: 44, color: BLUE, flexShrink: 0 }}
                                    >
                                        {isActive
                                            ? <VolumeUpRoundedIcon sx={{ fontSize: 18, animation: `${speakerPulse} 900ms ease-in-out infinite` }} />
                                            : <PlayArrowRoundedIcon sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}
